import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Easing,
  Linking,
  Modal,
  Pressable,
  TouchableOpacity,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import useRevenueCat, { RC_TEAM_ENTITLEMENT_ID } from '@hooks/useRevenueCat';
import theme from '@themes/theme';

export interface SubscribeModalProps {
  onPrimary: () => void;
  onRestore: () => void;
  onDismiss?: () => void;
}

// Apple rejected this modal (Guideline 3.1.2(c)) for hiding the billed amount
// when RevenueCat offerings hadn't loaded yet. Fall back to the known
// TaskTag Monthly price so the price/renewal line is never blank.
const FALLBACK_MONTHLY_PRICE = 20;

export function SubscribeModal({
  onPrimary,
  onDismiss,
  onRestore,
}: Readonly<SubscribeModalProps>) {
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const cardScale = useRef(new Animated.Value(0.88)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;
  const actionsAnim = useRef(new Animated.Value(0)).current;
  const actionsTranslateY = actionsAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [theme.spacing.xs + theme.spacing.xxxs, 0],
  });
  const iconSize = theme.spacing.xxl + theme.spacing.m;

  const insets = useSafeAreaInsets();
  const { t } = useTranslation('models');
  const { billedMonthlyCost, offerings, offeringsLoading } = useRevenueCat();

  // Mirrors the package lookup in useRevenueCat's startSubscription — until
  // this resolves true, tapping Subscribe cannot open the Apple purchase
  // sheet, so keep the CTA blocked/loading instead of a nonfunctional enabled
  // button (TA-10254 QA finding).
  const monthlyPackageReady = !!offerings?.all?.[
    'monthly_offering'
  ]?.availablePackages?.some((p) => p.identifier === RC_TEAM_ENTITLEMENT_ID);

  // Keeps the Subscribe button spinning from tap until the caller's
  // onPrimary either bails out early or hands off to the native purchase
  // sheet — the modal itself stays mounted throughout (TA-10186 QA finding:
  // the modal used to close before the iOS subscribe sheet ever appeared).
  const [isPurchasing, setIsPurchasing] = useState(false);

  // Leading-edge debounce so a rapid double-tap on the CTA can't fire
  // onPrimary twice; the ref keeps the wrapper reading the fresh callback.
  const onPrimaryRef = useRef(onPrimary);
  onPrimaryRef.current = onPrimary;
  const handlePrimaryPress = useMemo(
    () =>
      debounce(
        async () => {
          setIsPurchasing(true);
          try {
            await onPrimaryRef.current();
          } finally {
            setIsPurchasing(false);
          }
        },
        1000,
        {
          leading: true,
          trailing: false,
        }
      ),
    []
  );
  useEffect(() => () => handlePrimaryPress.cancel(), [handlePrimaryPress]);

  // Same leading-edge debounce for Restore Purchases — a double-tap would
  // otherwise fire two concurrent restorePurchases calls.
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  const handleRestorePress = useMemo(
    () =>
      debounce(() => onRestoreRef.current(), 1000, {
        leading: true,
        trailing: false,
      }),
    []
  );
  useEffect(() => () => handleRestorePress.cancel(), [handleRestorePress]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(backdropOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.spring(cardScale, {
        toValue: 1,
        damping: 20,
        stiffness: 260,
        useNativeDriver: true,
      }),
      Animated.timing(cardOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      // CTA appears shortly after checkmark finishes (40ms delay + 220ms draw = 260ms)
      Animated.timing(actionsAnim, {
        toValue: 1,
        duration: 150,
        delay: 300,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  return (
    <Modal visible transparent animationType='none' onRequestClose={onDismiss}>
      {/* Backdrop */}
      <Pressable
        onPress={onDismiss}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
        }}>
        <Animated.View
          style={
            {
              flex: 1,
              backgroundColor: theme.colors.black,
              opacity: Animated.multiply(backdropOpacity, 0.45),
            } as any
          }
        />
      </Pressable>
      <Animated.View
        style={
          {
            position: 'absolute',
            bottom: theme.spacing.l + theme.spacing.m + insets.bottom,
            left: theme.spacing.m,
            right: theme.spacing.m,
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadii.m + theme.borderRadii.xxs,
            paddingTop: theme.spacing.l + theme.spacing.m,
            paddingBottom: theme.spacing.l + theme.spacing.m,
            overflow: 'hidden',
            zIndex: 60,
            opacity: cardOpacity,
            transform: [{ scale: cardScale }],
          } as any
        }>
        {/* Icon/title are currently fixed for the personal plan. */}
        <Box alignItems='center' mb='xxs'>
          <Box
            alignItems='center'
            justifyContent='center'
            style={{
              borderRadius: theme.borderRadii.m - theme.borderRadii.xxxs,
            }}
            backgroundColor='greenSecondaryMild'
            width={iconSize}
            height={iconSize}>
            <Icon
              name='CrownOutline'
              variant='xxl'
              color='greenSecondary'></Icon>
          </Box>
        </Box>
        <Box mt='m' mx='l' alignItems='center' mb='xxs'>
          <Text variant='heading2' color='textPrimary' textAlign='center'>
            {t('payments.personalPlan.name')}
          </Text>
          <Text color='textSecondary' mt='xs' textAlign='center' mb='xxs'>
            <Text variant='labelEmphasized' color='greenSecondary'>
              {t('payments.personalPlan.priceLabel', {
                monthlyCost: (
                  billedMonthlyCost ?? FALLBACK_MONTHLY_PRICE
                ).toFixed(0),
              })}
            </Text>
            {t('payments.personalPlan.renewalNotice')}
          </Text>
          <Box
            backgroundColor='grey01'
            borderRadius='xs'
            p='s'
            mt='m'
            mb='xxs'
            width='100%'>
            {(
              t('payments.personalPlan.benefits', {
                returnObjects: true,
              }) as string[]
            ).map((item, index) => (
              <Box
                flexDirection='row'
                mt={index > 0 ? 'xs' : 'none'}
                key={item}>
                <Icon name='Check' color='greenSecondary'></Icon>
                <Text variant='webBodyHintSubline' color='textPrimary' ml='xs'>
                  {item}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
        <Animated.View
          style={
            {
              marginHorizontal: theme.spacing.l,
              marginTop: theme.spacing.m,
              gap: theme.spacing.xs,
              opacity: actionsAnim,
              transform: [{ translateY: actionsTranslateY }],
            } as any
          }>
          <Button
            fullWidth={true}
            backgroundColor='black'
            variant='create'
            isSmall={true}
            height={theme.spacing.xxl}
            disabled={!monthlyPackageReady || isPurchasing}
            isLoading={
              (offeringsLoading && !monthlyPackageReady) || isPurchasing
            }
            onPress={handlePrimaryPress}>
            <Text variant='webSmall' color='white'>
              {t('payments.subscribe')}
            </Text>
          </Button>
        </Animated.View>
        <Button
          marginTop='s'
          fullWidth={true}
          borderColor='white'
          backgroundColor='white'
          variant='create'
          isSmall={true}
          height={theme.spacing.xxl}
          onPress={handleRestorePress}>
          <Text variant='webSmall' color='grey05'>
            {t('payments.restorePurchases')}
          </Text>
        </Button>
        <Box
          marginTop='xxxs'
          flexDirection='row'
          height={theme.spacing.xxl}
          alignItems='center'
          justifyContent='center'>
          <TouchableOpacity
            onPress={async () => {
              await Linking.openURL(
                'https://www.tasktag.com/terms-and-conditions'
              );
            }}>
            <Box
              height={theme.spacing.l + theme.spacing.m + theme.spacing.xxs}
              justifyContent='center'>
              <Text variant='metadata' color='grey04'>
                {t('payments.termsOfUse')}
              </Text>
            </Box>
          </TouchableOpacity>
          <Box
            width={theme.spacing.xxs}
            height={theme.spacing.xxs}
            borderRadius='xxxs'
            backgroundColor='grey04'
            style={{ marginHorizontal: theme.spacing.xs + theme.spacing.xxxs }}
            alignItems='center'
            justifyContent='center'></Box>
          <TouchableOpacity
            onPress={async () => {
              await Linking.openURL('https://www.tasktag.com/privacy');
            }}>
            <Box
              height={theme.spacing.l + theme.spacing.m + theme.spacing.xxs}
              justifyContent='center'>
              <Text variant='metadata' color='grey04'>
                {t('payments.privacyPolicy')}
              </Text>
            </Box>
          </TouchableOpacity>
        </Box>
      </Animated.View>
    </Modal>
  );
}
